import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMatches, useRouter } from '@tanstack/react-router';
import { authorize, authorizeAll, type AuthorizationResult } from './authorization';
import { saveUserPreferences } from './configuration-api';
import { useConfigurationContext } from './configuration-context';
import {
  featureConfigurationQueryOptions,
  invalidateFeatureConfiguration,
  layoutQueryOptions,
} from './configuration-queries';
import { visibleHeaderItems, visibleSidebarSections, type SidebarOptions } from './navigation';
import {
  extractPaging,
  extractSubFeatureTexts,
  extractTranslations,
  pickTextGroups,
  pickTexts,
  type PagingPreset,
} from './presentation';
import { featureKeyFromMatches } from './route-feature';
import type { ConfigurationScope } from './types';

/**
 * React access to the configuration — the replacement for injecting `ConfigurationState` /
 * `LayoutState`. Nothing here loads "the active feature" as a side effect of navigation the way
 * the Angular effects did: a screen asks for its own feature's configuration, and the query
 * cache shares it between everything that asks.
 */

/** Stands in for a missing scope so query options can be built; such queries are disabled. */
const NO_SCOPE: ConfigurationScope = { userId: '', orgNodeId: null, viewMode: 'admin' };

/** The feature key the current route declares (`staticData.featureKey`), or null. */
export function useActiveFeatureKey(): string | null {
  return useMatches({ select: matches => featureKeyFromMatches(matches) });
}

/**
 * One feature's resolved configuration at the selected org node. `feature` defaults to the
 * active route's. Idle (not loading) while logged out, before an org node is selected, or on a
 * route with no feature.
 */
export function useFeatureConfiguration(feature?: string) {
  const { client, scope } = useConfigurationContext();
  const activeFeature = useActiveFeatureKey();
  const key = feature ?? activeFeature;
  const options = featureConfigurationQueryOptions(client, key ?? '', scope ?? NO_SCOPE);
  return useQuery({
    ...options,
    enabled: key !== null && scope !== null && scope.orgNodeId !== null,
  });
}

/**
 * A feature's user-visible strings: `presentation.texts.<group>` (or `presentation.texts` when
 * `group` is null), already translated by the backend, narrowed to `keys`. `keys` should be a
 * module-level constant so the result is stable between renders. `query` is the configuration
 * query, for its loading and error states.
 */
export function useFeatureTexts<K extends string>(
  feature: string,
  group: string | null,
  keys: readonly K[],
) {
  const query = useFeatureConfiguration(feature);
  const texts = useMemo(
    () =>
      pickTexts(
        group === null
          ? extractTranslations(query.data)
          : extractSubFeatureTexts(query.data, group),
        keys,
      ),
    [query.data, group, keys],
  );
  return { texts, query };
}

/** Several of a feature's text groups at once (`{ group: keys }`); see {@link useFeatureTexts}. */
export function useFeatureTextGroups<G extends Record<string, readonly string[]>>(
  feature: string,
  groups: G,
) {
  const query = useFeatureConfiguration(feature);
  const texts = useMemo(() => pickTextGroups(query.data, groups), [query.data, groups]);
  return { texts, query };
}

/** This app's layout (navigation + the user-level capability summary). */
export function useLayout() {
  const { client, appId, scope } = useConfigurationContext();
  return useQuery({
    ...layoutQueryOptions(client, appId, scope ?? NO_SCOPE),
    enabled: scope !== null,
  });
}

/**
 * Set `data-theme` on the document root from the active feature's configuration, as the
 * Angular service did on every load. Mount once, in the app shell.
 */
export function useConfigurationTheme(): void {
  const theme = useFeatureConfiguration().data?.theme;
  useEffect(() => {
    if (theme) {
      document.documentElement.setAttribute('data-theme', theme);
    }
  }, [theme]);
}

/**
 * May the user do `key` (a summary capability like `create`, or a Permission like
 * `lead:update`)? See `authorize` for the rules.
 */
export function useAuthorization(key: string): AuthorizationResult {
  return useAuthorizationAll([key]);
}

/** All of `keys`. */
export function useAuthorizationAll(keys: readonly string[]): AuthorizationResult {
  const { isGlobalAdmin, scope, permissions } = useConfigurationContext();
  const layout = useLayout().data;
  const context = {
    isGlobalAdmin,
    viewMode: scope?.viewMode ?? 'admin',
    layout,
    permissions,
  } as const;
  return keys.length === 1 ? authorize(context, keys[0]!) : authorizeAll(context, keys);
}

/** The full paths of every route in this app's tree — what nav items are checked against. */
export function useRoutePaths(): string[] {
  const router = useRouter();
  return Object.keys(router.routesByPath);
}

/** The sidebar to render, and the layout query's state for loading/error handling. */
export function useSidebar(options: SidebarOptions = {}) {
  const layout = useLayout();
  const routePaths = useRoutePaths();
  return {
    ...layout,
    sections: visibleSidebarSections(layout.data, routePaths, options),
    headerItems: visibleHeaderItems(layout.data),
  };
}

/**
 * Save the user's preferences for a feature, then refetch that feature's configuration at the
 * selected org node in every view mode (preferences are a resolution tier).
 */
export function useSaveUserPreferences(feature: string) {
  const { client, scope } = useConfigurationContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (preferences: Record<string, unknown>) => {
      if (!scope) {
        throw new Error('Cannot save preferences while logged out');
      }
      await saveUserPreferences(client, scope.userId, feature, preferences);
      return scope;
    },
    onSuccess: savedFor => invalidateFeatureConfiguration(queryClient, savedFor, feature),
  });
}

export interface PageState {
  /** 0-based. */
  pageIndex: number;
  pageSize: number;
}

/**
 * A list's paging, with the page sizes from the feature's configuration (see
 * {@link extractPaging}); `fallback` should be a module-level constant. Until the user picks a
 * size the list follows the configured default, so a configuration that loads after the first
 * render still applies.
 */
export function useFeaturePaging(feature: string, fallback: PagingPreset) {
  const configuration = useFeatureConfiguration(feature).data;
  const preset = useMemo(() => extractPaging(configuration, fallback), [configuration, fallback]);
  const [state, setState] = useState<{ pageIndex: number; pageSize: number | null }>({
    pageIndex: 0,
    pageSize: null,
  });
  const page: PageState = {
    pageIndex: state.pageIndex,
    pageSize: state.pageSize ?? preset.defaultSize,
  };
  const { defaultSize } = preset;
  const setPage = useCallback(
    (next: PageState | ((current: PageState) => PageState)) =>
      setState(current => {
        const resolved = { ...current, pageSize: current.pageSize ?? defaultSize };
        return typeof next === 'function' ? next(resolved) : next;
      }),
    [defaultSize],
  );
  return { page, setPage, pageSizeOptions: preset.options };
}
